/**
 * Posting an order.
 *
 * The kiosk holds the cart and posts it **once**, with its lines already priced.
 * The old `sales` router held a server-side mutable cart — `addToCart`,
 * `updateQuantity`, `removeItem`, `clearCart`, each a round trip that wrote a
 * row. Free over Electron IPC on one machine; a customer waiting on the network
 * to add vanilla over HTTPS.
 *
 * The server re-prices what arrives and refuses a mismatch, which is why both
 * sides import `@mgc/domain` rather than each having a copy of the arithmetic.
 */
import { z } from 'zod'
import { zId, zInstant, zMoney, zOrderType, zPaymentMethod, zPaymentState, zSku, zTemperature } from './scalars'

export const zLineSelection = z.object({
  /** Minted on the kiosk, so a replay is an upsert. */
  id: zId,
  productId: zSku,
  quantity: z.number().int().min(1).max(99),
  temperature: zTemperature.optional(),
  changeCode: zSku.optional(),
  /** May repeat — two extra shots. */
  addCodes: z.array(zSku).max(20),
  orderType: zOrderType.optional(),
  note: z.string().max(120).optional()
})

/**
 * What the page believes each line costs.
 *
 * Sent so the server can *disagree* rather than to be trusted. A mismatch is a
 * stale catalogue on one side, which is a real condition and reads as one —
 * not as a customer being overcharged.
 */
export const zClaimedLine = z.object({ id: zId, total: zMoney })

export const zCompanyReceipt = z.object({
  registrationNumber: z.string().min(5).max(20),
  name: z.string(),
  tin: z.string().optional()
})

/**
 * Whatever the coupon says. It was twelve letters and digits, and real coupons
 * turned out shorter — so the shape is 1C's to judge, and this only refuses an
 * empty code and one long enough to be something other than a coupon.
 */
export const zCoupon = z.string().trim().min(1).max(64)

export const zCreateOrder = z.object({
  /** UUIDv7, minted on the kiosk. The idempotency key for the whole order. */
  id: zId,
  orderType: zOrderType,
  lines: z.array(zLineSelection).min(1).max(60),
  claimed: z.object({ total: zMoney, lines: z.array(zClaimedLine) }),
  company: zCompanyReceipt.nullable().default(null),
  note: z.string().max(240).optional(),
  /** A coupon the customer typed or scanned, sent to 1C as `coupon`. */
  coupon: zCoupon.optional(),
  placedAt: zInstant,
  /**
   * The method the customer tapped, for the 1C reservation made on this call.
   *
   * 1C posts a sale from the reservation's `typeofpayment`, not the paid
   * call's, so it has to be right before any money moves. Optional because a
   * kiosk that predates it, and an outbox replay whose method is already in
   * `settledPayments`, send none; the server then reserves as `card`.
   */
  method: zPaymentMethod.optional(),
  /**
   * A payment already taken on the counter, replayed from the outbox.
   *
   * This is the field that makes an offline card sale safe: the terminal
   * approved before the network came back, so the approval arrives with the
   * order rather than after it. See `docs/offline.md`.
   */
  settledPayments: z
    .array(
      z.object({
        id: zId,
        method: zPaymentMethod,
        amount: zMoney,
        reference: z.string().optional(),
        detail: z.record(z.string(), z.string()).optional(),
        capturedAt: zInstant
      })
    )
    .max(6)
    .default([])
})

export const zAddPayment = z.object({
  orderId: zId,
  paymentId: zId,
  method: zPaymentMethod,
  amount: zMoney,
  reference: z.string().optional(),
  detail: z.record(z.string(), z.string()).optional()
})

export const zSetPaymentState = z.object({
  orderId: zId,
  paymentId: zId,
  state: zPaymentState,
  reference: z.string().optional(),
  detail: z.record(z.string(), z.string()).optional()
})

/**
 * A Mongolian phone number: eight digits, no country code, no spaces.
 *
 * That is the whole check. Which prefixes are mobile is the operators' to
 * change, and 1C is the one that has to deliver to it.
 */
export const zPhone = z.string().regex(/^\d{8}$/)

/**
 * The customer's number for a sale whose e-Barimt 1C is holding. The order
 * names the sale; the server finds 1C's `document_id` itself, because a kiosk
 * that could name one could attach a phone to any sale in the shop.
 */
export const zEbarimtPhone = z.object({ orderId: zId, phone: zPhone })

/** The order as the kiosk gets it back: enough for a receipt and a success screen. */
export const zOrderView = z.object({
  id: zId,
  sequence: z.number().int(),
  displayNumber: z.string(),
  status: z.enum(['open', 'paid', 'void']),
  orderType: zOrderType,
  gross: zMoney,
  net: zMoney,
  vat: zMoney,
  savings: zMoney,
  due: zMoney,
  paid: zMoney,
  change: zMoney,
  placedAt: z.string(),
  completedAt: z.string().nullable(),
  company: zCompanyReceipt.nullable(),
  lines: z.array(
    z.object({
      id: zId,
      name: z.string(),
      baseSku: zSku,
      effectiveSku: zSku,
      quantity: z.number().int(),
      unitPrice: zMoney,
      total: zMoney,
      temperature: zTemperature.nullable(),
      changeLabel: z.string().nullable(),
      note: z.string().nullable(),
      addOns: z.array(z.object({ label: z.string(), quantity: z.number().int(), total: zMoney }))
    })
  ),
  payments: z.array(
    z.object({
      id: zId,
      method: zPaymentMethod,
      amount: zMoney,
      state: zPaymentState,
      reference: z.string().nullable()
    })
  ),
  fiscal: z
    .object({
      state: z.enum(['queued', 'filed', 'deferred', 'failed', 'skipped']),
      lottery: z.string().nullable(),
      billId: z.string().nullable(),
      qrData: z.string().nullable(),
      /** 1C's own check number, once the filing has landed. The receipt prints it in place of ours. */
      onecNumber: z.number().int().nullable(),
      /**
       * `deferred` only: 1C asked for a phone number to send the e-Barimt to,
       * and `phoneSent` is whether it has one. The success screen asks while
       * the first is true and the second is not.
       */
      phoneWanted: z.boolean(),
      phoneSent: z.boolean()
    })
    .nullable()
})

export type CreateOrderInput = z.infer<typeof zCreateOrder>
export type OrderView = z.infer<typeof zOrderView>

/**
 * Why the server refused.
 *
 * Named, because "order failed" on a kiosk with a customer's card in a terminal
 * is not an error message, it is a support call.
 */
export const ORDER_REFUSAL = {
  branchClosed: 'branch-closed',
  priceMismatch: 'price-mismatch',
  unknownProduct: 'unknown-product',
  outOfStock: 'out-of-stock',
  noShift: 'no-shift',
  alreadyExists: 'already-exists'
} as const

export type OrderRefusal = (typeof ORDER_REFUSAL)[keyof typeof ORDER_REFUSAL]
